import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faList,
  faTrash,
  faEdit,
  faDollarSign,
  faFlagCheckered,
  faHexagonExclamation,
  faCircleXmark,
  faRefresh,
  faCircleCheck,
  faWindowMinimize,
  faCirclePlus,
  faClonePlus, faTrashPlus,
} from '@/fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { DayTradeHistoryEntry, DayTradeHistoryEntryType } from '@/models/day-trading-session';

const HISTORY_TYPE_ICONS: Record<DayTradeHistoryEntryType, [IconDefinition, string|null]> = {
  ADD_TRADE:               [faCirclePlus, 'text-gray-700 dark:text-gray-100'],
  ADD_ADJUSTEMENT:         [faClonePlus,  'text-gray-700 dark:text-gray-100'],

  DELETE_TRADE:            [faTrash,     'text-red-300 dark:text-red-300'],
  DELETE_ADJUSTEMENT:      [faTrashPlus, 'text-red-300 dark:text-red-300'],

  EDIT_TRADE_TARGET:       [faEdit,       null],
  EDIT_ADJUSTEMENT_PRICE:  [faDollarSign, null],

  TRADE_GOAL_REACHED:      [faFlagCheckered,      'text-green-600 dark:text-green-600'],
  TRADE_STOP_LOSS:         [faHexagonExclamation, 'text-red-600   dark:text-red-500'],
  TRADE_STOPPED:           [faCircleXmark,        null],
  TRADE_REOPENED:          [faRefresh,            null],

  ADJUSTEMENT_EXECUTED:    [faCircleCheck, null],
  ADJUSTEMENT_UNEXECUTED:  [faCircleXmark, null],
};

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

interface HistoryPanelProps {
  history: DayTradeHistoryEntry[];
  onMinimize?: () => void;
}

export default function HistoryPanel({ history, onMinimize }: HistoryPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(history.length);

  useEffect(() => {
    if (history.length > prevLengthRef.current && scrollRef.current !== null) {
      scrollRef.current.scrollTop = 0;
    }
    prevLengthRef.current = history.length;
  }, [history.length]);

  const displayHistory = [...history]
    .sort((a,b) => -1 * a.timestamp.localeCompare(b.timestamp))
    .filter(p => !['ADJUSTEMENT_EXECUTED', 'ADJUSTEMENT_UNEXECUTED'].includes(p.type));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faList} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="font-semibold text-gray-900 dark:text-white text-sm">Historie</span>
        </div>
        <div className="flex items-center gap-2">
          {onMinimize !== undefined && (
            <button
              onClick={onMinimize}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer transition-colors"
              title="Minimieren"
            >
              <FontAwesomeIcon icon={faWindowMinimize} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="overflow-y-auto max-h-96">
        {displayHistory.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
            Noch nichts passiert.
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {displayHistory.map((entry) => (
              <div key={entry.id} className="px-4 py-2 flex items-center gap-2 text-sm">
                <FontAwesomeIcon icon={(HISTORY_TYPE_ICONS[entry.type])[0]} className={`w-3.5 h-3.5 ${ ((HISTORY_TYPE_ICONS[entry.type])[1] ?? 'text-gray-400 dark:text-gray-500') } shrink-0`} />
                <span className="font-bold text-gray-900 dark:text-white shrink-0 min-w-[2.25rem]">{entry.symbol}</span>
                <span className="text-gray-600 dark:text-gray-300 truncate flex-1 min-w-0">{entry.text}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0 tabular-nums">{formatTime(entry.timestamp)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
